import type { ReactNode } from 'react';
import type { MentionNames } from '@/lib/mentions';

/** Custom emoji, role / user mentions, @everyone / @here. */
const TOKENS = /<(a?):(\w+):(\d+)>|<@&(\d+)>|<@!?(\d+)>|@(everyone|here)\b/g;

const MENTION = 'rounded px-0.5 font-medium';
const BLURPLE = { color: '#c9cdfb', backgroundColor: 'rgba(88, 101, 242, 0.3)' };

/**
 * Message text as the Discord client shows it in the previews: custom emoji
 * codes (`<:name:id>`, `<a:name:id>`) become inline images from Discord's CDN,
 * mentions become highlighted names (roles in their colour) — with `names`,
 * else a generic label. Everything else stays plain text.
 */
export function renderDiscordText(text: string | undefined, names?: MentionNames): ReactNode {
  if (!text) return text;
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKENS)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const [, animated, emojiName, emojiId, roleId, userId, everyone] = m;
    if (emojiId) {
      out.push(
        <img
          key={at}
          src={`https://cdn.discordapp.com/emojis/${emojiId}.${animated ? 'gif' : 'png'}`}
          alt={`:${emojiName}:`}
          title={`:${emojiName}:`}
          className="inline-block h-[1.375em] w-[1.375em] object-contain align-[-0.3em]"
        />
      );
    } else if (roleId) {
      const role = names?.roles.get(roleId);
      const hex = role?.color ? `#${role.color.toString(16).padStart(6, '0')}` : null;
      out.push(
        <span key={at} className={MENTION} style={hex ? { color: hex, backgroundColor: `${hex}1f` } : BLURPLE}>
          @{role?.name ?? 'Rolle'}
        </span>
      );
    } else if (userId) {
      out.push(
        <span key={at} className={MENTION} style={BLURPLE}>
          @{names?.users.get(userId) ?? 'Person'}
        </span>
      );
    } else {
      out.push(
        <span key={at} className={MENTION} style={BLURPLE}>
          @{everyone}
        </span>
      );
    }
    last = at + m[0].length;
  }
  if (last === 0) return text;
  out.push(text.slice(last));
  return out;
}
